import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable ,  Subject } from 'rxjs';
import { ExceptionService, PaginatedList, UtilsService } from '../../xform-compat';
import { RmsBasicCrudService } from '../components/rms-basic-crud.service';
import { Note } from '../../shared/models';
import { ContentService } from '../services';

/**
 * Injectable service for insights
 */
@Injectable({
  providedIn: 'root'
})
export class InsightsService extends RmsBasicCrudService<any> {
  private insightsUpdate: Subject<any>;
  private readonly insightsEmailList: Array<Note>;

  public insightsEmailList$ = new Subject<null>();

  constructor(
    http: HttpClient,
    utilsService: UtilsService,
    exceptionService: ExceptionService,
    private contentService: ContentService
  ) {
    super(http, utilsService, exceptionService);
    this.insightsUpdate = new Subject<any>();
    this.insightsEmailList = [];
  }

  /**
   * Returns the list of insights selected for emailing.
   */
  public getInsightsEmailList(): Array<Note> {
    return this.insightsEmailList;
  }

  /**
   * Adds an item to the email list
   * @param item
   */
  public addInsightToEmailList(item: Note): void {
    const itemIndex = this.insightsEmailList.findIndex(insight => insight.id === item.id);
    if ( itemIndex >= 0 ) {
      this.insightsEmailList[itemIndex] = item;
    } else {
      this.insightsEmailList.push(item);
    }
    this.insightsEmailListUpdated();
  }

  /**
   * Removes an item from the email list
   * @param item
   */
  public removeInsightFromEmailList(item: Note): void {
    const itemIndex = this.insightsEmailList.findIndex(insight => insight.id === item.id);
    if ( itemIndex >= 0 ) {
      this.insightsEmailList.splice(itemIndex, 1);
    }
    this.insightsEmailListUpdated();
  }

  /**
   * On insightsEmailList updates
   */
  public insightsEmailListUpdated () {
    this.insightsEmailList$.next(undefined);
  }

  /**
   * Get insights
   * @param params
   */
  public getInsights (params?: any): Observable<PaginatedList<Note>> {
    return this.contentService.formatInlineImageObservablePaginatedNote(super.getData(`/api/rms/insights`, params));
  }

  /**
   * Get insights
   * @param id
   * @param params
   */
  public getInsightsById (id: number, params?: any): Observable<Note> {
    return super.getData(`/api/rms/insights/${id}`, params);
  }

  /**
   * Create a new insights
   * @param body
   */
  public createInsight (body: {content: string, attachmentIds: Array<number>}): Observable<Note> {
    body.content = this.contentService.formatInlineImageContentRemove(body.content);
    return super.saveData(`/api/rms/insights`, body);
  }

  /**
   * Creates a new insight draft
   * @param body
   */
  public createInsightDraft ( body: {content: string, attachmentIds: Array<number>}): Observable<Note> {
    body.content = this.contentService.formatInlineImageContentRemove(body.content);
    return super.saveData(`/api/rms/insights/draft`, body);
  }

  /**
   * Autocompletes the insights authors to filter insights
   * @param params
   */
  public getAuthors (params?: any): Observable<PaginatedList<{userId: number, displayName: string}>> {
    return super.getData(`/api/rms/insights/authors`, params);
  }

  /**
   * Send insights email
   * @param params
   */
  public sendEmail (params?: any): Observable<Note> {
    return super.saveData(`/api/rms/insights/email`, params);
  }

  /**
   * Search insights by search term
   * @param body
   */
  public searchInsights (body: any): Observable<PaginatedList<Note>> {
    return super.saveData(`/api/rms/insights/search`, body);
  }

  /**
   * Edit and existing insights and save a draft
   * @param idInsight
   * @param body
   */
  public saveDraftForPosted (idInsight: number, body: any): Observable<Note> {
    body.content = this.contentService.formatInlineImageContentRemove(body.content);
    return super.saveData(`/api/rms/insights/${idInsight}/draft`, body);
  }

  /**
   * Post a new version of the insights
   * @param idInsight
   * @param body
   */
  public updateInsight (idInsight: number, body: any): Observable<Note> {
    body.content = this.contentService.formatInlineImageContentRemove(body.content);
    return super.updateData(`/api/rms/insights/${idInsight}`, body);
  }

  /**
   * Updates insight object references
   * @param idInsight
   * @param body
   */
  public updateInsightReferences (idInsight: number, body: any): Observable<Note> {
    return super.updateData(`/api/rms/insights/${idInsight}/references`, body);
  }

  /**
   * Get user insight drafts
   * @param params
   */
  public getInsightsDrafts (params?: any): Observable<PaginatedList<Note>> {
    return this.contentService.formatInlineImageObservablePaginatedNote(super.getData(`/api/rms/insights/draft/me`, params));
  }

  /**
   * Get user insight maps
   */
  public getInsightsMaps (params?: any): Observable<PaginatedList<any>> {
    return super.getData(`/api/rms/insights/maps`, params);
  }

  /**
   * Get user insight maps for specific insight
   */
  public getSingleInsightMaps (idInsight: number, params?: any): Observable<any> {
    return super.getData(`/api/rms/insights/${idInsight}/maps`, params);
  }

  /**
   * Post user insight maps
   */
  public postInsightsMaps (idInsight, params): Observable<PaginatedList<any>> {
    return super.updateData(`/api/rms/insights/${idInsight}/maps`, params);
  }

  /**
   * Discard draft insight
   * @param idInsight
   */
  public discardInsightDraft (idInsight: number): Observable<null> {
    return super.deleteData(`/api/rms/insights/${idInsight}/draft/me`);
  }

  /**
   * Sends a message to the subscribed object on the component
   * @param message
   */
  public sendMessage(message: string) {
    this.insightsUpdate.next({ text: message });
  }

  /**
   * Observable to listen when a message is sent
   */
  public getMessage(): Observable<any> {
    return this.insightsUpdate.asObservable();
  }
}
